import { useState, type ReactElement } from "react";
import {
  Activity, AlertTriangle, CreditCard, Database, LayoutDashboard,
  Package, Settings, Shield, Users as UsersIcon,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { cn } from "@/lib/utils";
import { OverviewTab } from "@/components/admin/OverviewTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { TeamsTab } from "@/components/admin/TeamsTab";
import { ProvidersTab } from "@/components/admin/ProvidersTab";
import { ModelsTab } from "@/components/admin/ModelsTab";
import { CreditsTab } from "@/components/admin/CreditsTab";
import { AuditTab } from "@/components/admin/AuditTab";
import { OperationsTab } from "@/components/admin/OperationsTab";

type Tab = "overview" | "users" | "teams" | "providers" | "models" | "credits" | "audit" | "operations";

const tabItems: Array<{ key: Tab; icon: typeof LayoutDashboard; label: string }> = [
  { key: "overview", icon: LayoutDashboard, label: "Overview" },
  { key: "users", icon: UsersIcon, label: "Users" },
  { key: "teams", icon: Shield, label: "Teams" },
  { key: "providers", icon: Package, label: "Providers" },
  { key: "models", icon: Database, label: "Models" },
  { key: "credits", icon: CreditCard, label: "USD Wallets" },
  { key: "audit", icon: Activity, label: "Audit Log" },
  { key: "operations", icon: AlertTriangle, label: "Job recovery" },
];

const TAB_VIEWS: Record<Tab, () => ReactElement> = {
  overview: OverviewTab,
  users: UsersTab,
  teams: TeamsTab,
  providers: ProvidersTab,
  models: ModelsTab,
  credits: CreditsTab,
  audit: AuditTab,
  operations: OperationsTab,
};

export function AdminPage() {
  const [tab, setTab] = useState<Tab>("overview");
  const { user } = useAuthStore();

  if (!user?.is_superuser) {
    return (
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="mx-auto mb-3 h-12 w-12 text-studio-warning" />
          <p className="font-display text-lg font-semibold">Admin access required</p>
          <p className="mt-1 text-sm text-studio-muted">You need superuser privileges to view this page.</p>
        </div>
      </div>
    );
  }

  const ActiveView = TAB_VIEWS[tab];

  return (
    <div className="flex min-h-full min-w-0 flex-col lg:h-full lg:flex-row">
      <aside className="sticky top-0 z-20 w-full shrink-0 border-b border-studio-border bg-studio-panel p-2 lg:static lg:w-48 lg:border-b-0 lg:border-r lg:p-3">
        <div className="mb-2 flex items-center gap-2 px-2 pt-1 lg:mb-4 lg:pt-2">
          <Settings className="h-4 w-4 text-studio-accent" />
          <span className="font-display text-sm font-bold tracking-tight">Admin Panel</span>
        </div>
        <nav className="flex max-w-full gap-1 overflow-x-auto lg:block lg:space-y-0.5" aria-label="Admin sections">
          {tabItems.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "flex min-h-11 shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors lg:w-full",
                tab === key
                  ? "bg-studio-accent-soft font-medium text-studio-accent"
                  : "text-studio-muted hover:bg-studio-card/50 hover:text-studio-ink"
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 flex-1 p-4 sm:p-6 lg:overflow-y-auto">
        <ActiveView />
      </div>
    </div>
  );
}
